<template lang="pug">
.container.grid-xl.py-2.manual-page
  .columns
    .column.col-12
      router-link.btn.btn-link.btn-sm(:to="{ name: 'app.manuais' }")
        i.fa.fa-arrow-left.fa-fw
        |  Voltar aos manuais

      h4.h4 Configurações — OS-m: Formulários
      p.text-gray Manual do usuário · OS-m Admin v1.84+

      h5.h5 Visão geral
      p
        | Esta parte explica como
        strong  listar
        |  formulários e montar o
        strong  checklist
        |  usado no aplicativo: grupos, perguntas, mínimo de fotos, condições, referência e ações.
        |  O caminho é
        strong  Configurações → OS-m → Formulários
        | .

      .toast.toast-primary
        | Exige
        strong  Formulários → Listar
        | . Para incluir ou editar, use
        strong  Formulários → Criar
        |  e
        strong  Formulários → Alterar
        | . O botão
        strong  Logs
        |  exige
        strong  Logs → Listar
        | .

      h5.h5 1. Abrir Formulários
      ol
        li No menu lateral, clique em
          strong  Configurações
          | .
        li Expanda
          strong  OS-m
          |  e clique em
          strong  Formulários
          | .

      h5.h5 2. Listagem
      p
        | Filtre por
        strong  Pesquisa
        |  (descrição). A tabela mostra
        strong  ID
        |  e
        strong  Descrição
        | .
      figure.manual-figure
        img(:src="imgLista" alt="Listagem de formulários")
      ol
        li Clique no
          strong  +
          |  para criar ou no ícone
          strong  editar
          |  na linha.

      h5.h5 3. Descrição e tipo
      p
        | Informe
        strong  Descrição
        |  (nome do formulário) e
        strong  Tipo
        | . O tipo define o uso na ordem de serviço e quais ações e templates aparecem.
      ul
        li
          strong Vistoria padrão
        li
          strong Verificar notificação
          | ,
          strong  Verificar notificação sem valor
          |  e
          strong  Verificar prazo para regularizar
        li
          strong Verificar desconto
          |  e
          strong  Verificar autuação
        li
          strong Verificar alvará vencido
          |  e
          strong  Alvará - concluir vistoria
        li
          strong Entregar documento
          |  e
          strong  Notificar
      p
        | Na edição, use
        strong  Duplicar formulário
        |  para clonar. O sistema remove o ID, prefixa a descrição com “Cópia - ” e abre o registro como novo.

      h5.h5 4. Grupos
      p
        | O checklist é uma lista de
        strong  grupos
        | . Cada grupo reúne perguntas de um mesmo assunto (por exemplo, conservação do imóvel).
      ol
        li Clique no
          strong  +
          |  roxo abaixo da lista para
          strong  Adicionar grupo
          | .
        li Preencha
          strong  Título do grupo
          | .
        li Clique na seta à esquerda do título para
          strong  Expandir
          |  ou recolher as perguntas.
        li Arraste o ícone de pontos para reordenar os grupos.
        li No canto do grupo:
          strong  Condição
          |  (olho),
          strong  Duplicar
          |  e
          strong  Remover grupo
          | .

      h5.h5 5. Perguntas
      p
        | Com o grupo expandido, clique no
        strong  +
        |  interno (
        strong  Adicionar pergunta
        | ). Cada pergunta tem título, tipo e, no rodapé, mínimo de fotos, condição, referência, duplicar, remover e obrigatório.
      figure.manual-figure
        img(:src="imgChecklist" alt="Grupos e perguntas do checklist")
      p Tipos disponíveis:
      ul
        li
          strong Texto livre
          |  — resposta digitada.
        li
          strong Sim/Não
          |  — duas opções fixas.
        li
          strong Múltipla escolha
          |  — uma alternativa entre as cadastradas.
        li
          strong Caixas de seleção
          |  — várias alternativas ao mesmo tempo.
        li
          strong Número inteiro
          |  e
          strong  Número decimal
          | .
        li
          strong Data
          |  e
          strong  Horário
          | .
        li
          strong Local acessível
          |  — o fiscal indica se conseguiu acessar o local. Esta pergunta fica sempre
          strong  obrigatória
          | .
        li
          strong Notificar
          |  — dispara notificação a partir da resposta (veja a seção 12).
      p
        | Arraste o ícone de pontos da pergunta para reordenar dentro do grupo. Duplicar copia título, tipo e alternativas.

      h5.h5 6. Alternativas
      p
        | Em
        strong  Múltipla escolha
        |  e
        strong  Caixas de seleção
        | , informe o
        strong  Texto da alternativa
        |  (Parede, Teto, Piso). O
        strong  +
        |  inclui outra linha; a lixeira remove. A ordem das alternativas também pode ser arrastada.

      h5.h5 7. Mínimo de fotos
      p
        | O campo
        strong  Min fotos
        |  define quantas fotos o aplicativo exige para aquela pergunta.
      ol
        li No menu de três pontos da pergunta, marque ou desmarque
          strong  Mínimo de fotos
          | . Desmarcado, o campo some e a pergunta não cobra foto.
        li Com o campo visível, informe o número. O valor
          strong  0
          |  não exige foto.
        li Um valor maior (por exemplo,
          strong  2
          | ) faz o aplicativo pedir pelo menos essa quantidade antes de concluir a resposta.
      p
        | No exemplo, “O imóvel apresenta infiltração?” exige 2 fotos e está marcada como obrigatória.

      h5.h5 8. Obrigatório
      p
        | O interruptor
        strong  Obrigatório
        |  impede o fiscal de pular a pergunta.
        strong  Local acessível
        |  permanece obrigatória e o interruptor fica bloqueado.

      h5.h5 9. Condição
      p
        | A condição faz a pergunta (ou o grupo inteiro) aparecer no aplicativo somente se uma resposta anterior atender à regra. O ícone é o olho. Com condição gravada, o olho ganha um ponto laranja; sem condição, o ícone fica cortado.
      figure.manual-figure
        img(:src="imgCondicao" alt="Modal de condição da pergunta")
      ol
        li Clique no olho da pergunta ou do grupo. O título do modal é
          strong  Condição
          | .
        li Escolha o
          strong  Grupo
          |  e a
          strong  Pergunta
          | . Só entram perguntas anteriores, dos tipos
          strong  Múltipla escolha
          | ,
          strong  Sim/Não
          | ,
          strong  Número inteiro
          |  ou
          strong  Número decimal
          | . A condição de um grupo usa apenas grupos que estão acima dele.
        li Escolha a
          strong  Operação
          | .
          strong  Sim/Não
          |  e
          strong  Múltipla escolha
          |  aceitam
          strong  Igual =
          |  e
          strong  Diferente ≠
          | . Números também aceitam
          strong  Maior &gt;
          | ,
          strong  Maior Igual &gt;=
          | ,
          strong  Menor &lt;
          |  e
          strong  Menor Igual &lt;=
          | .
        li Informe a
          strong  Resposta
          |  esperada (Sim, Não, uma alternativa ou um número).
        li Confirme no botão verde. O botão vermelho apaga a condição.
      p
        | No exemplo, “Descreva a infiltração” só aparece se “O imóvel apresenta infiltração?” for igual a
        strong  Sim
        | .

      h5.h5 10. Referência
      p
        | A referência não esconde a pergunta. Ela registra, para o analista, qual resposta era esperada. O ícone de lista só aparece em
        strong  Sim/Não
        | ,
        strong  Múltipla escolha
        | ,
        strong  Caixas de seleção
        | ,
        strong  Número inteiro
        |  e
        strong  Número decimal
        | .
      figure.manual-figure
        img(:src="imgReferencia" alt="Modal de referência da pergunta")
      ol
        li Clique no ícone de lista (
          strong  Referência
          | ).
        li Em
          strong  Informações
          | , escreva um recado para o analista.
        li Em
          strong  Adicionar condições
          | , escolha a operação e o valor
          strong  Esperado
          | , depois clique no
          strong  +
          | .
        li Confirme em
          strong  Salvar
          | . A lixeira da linha remove aquela condição.
      p Operações, conforme o tipo:
      ul
        li
          strong Sim/Não
          | ,
          strong  número inteiro
          |  e
          strong  decimal
          | :
          strong  Espera que seja igual à
          |  e
          strong  Espera que seja diferente de
          | .
        li
          strong Número inteiro
          |  e
          strong  decimal
          | : também
          strong  dentro do intervalo
          | ,
          strong  fora do intervalo
          | ,
          strong  dentro de
          |  e
          strong  fora de
          |  (uma lista de valores).
        li
          strong Múltipla escolha
          |  e
          strong  Caixas de seleção
          | :
          strong  Espera que esteja dentro de
          |  e
          strong  Espera que esteja fora de
          | , marcando as alternativas.

      h5.h5 11. Ações
      p
        | O raio (
        strong  Ação
        | ) aparece quando o tipo do formulário tem ações e a pergunta é
        strong  Sim/Não
        | ,
        strong  Múltipla escolha
        | ,
        strong  Texto livre
        |  ou
        strong  Número inteiro
        | .
        strong  Vistoria padrão
        |  não oferece ações. Elas rodam quando o fiscal encerra a ordem de serviço, ligadas à resposta (Sim ou Não, uma alternativa, texto preenchido ou número diferente de zero).
      ul
        li
          strong Verificar notificação
          | : Atender, Autuar, Aplicar reincidência, Ignorar reincidência e Comentar.
        li
          strong Verificar desconto
          | : Conceder desconto, Negar desconto e Comentar.
        li
          strong Verificar autuação
          |  e
          strong  Verificar notificação sem valor
          | : Comentar; neste último, também Não Atender.
        li
          strong Verificar prazo para regularizar
          | : Comentar e Prorrogar prazo para regularizar.
        li
          strong Entregar documento
          | : Comentar e Ciência.
        li
          strong Alvará - concluir vistoria
          | : situações do alvará (em andamento, liberado definitivo ou provisório, não liberado, não concluído, cancelado, indeferido, liberdade econômica e local fechado).

      h5.h5 12. Pergunta do tipo Notificar
      p
        | Com o tipo
        strong  Notificar
        | , a pergunta ganha o interruptor
        strong  Notificação livre
        | . Desligado, escolha os modelos de documento. Ligado, aparece
        strong  Múltiplas notificações
        | .

      h5.h5 13. Templates
      p
        | Se o tipo do formulário tiver modelo pronto, surge
        strong  Usar template
        |  ao lado do título Checklist. Confirmar substitui o checklist atual.
      ul
        li
          strong Verificar notificação
          |  — template Verificar notificação.
        li
          strong Verificar desconto
          |  — template Verificar desconto.
        li
          strong Entregar documento
          |  — template Entregar Documentos.
        li
          strong Verificar alvará vencido
          |  — template Verificar Alvará Vencido.
        li
          strong Alvará - concluir vistoria
          |  — template Alvará de Funcionamento.

      h5.h5 14. Salvar
      ol
        li Clique em
          strong  Salvar
          | . Título de grupo, título de pergunta e alternativas em branco impedem a gravação.
        li No registro já salvo, use
          strong  Logs
          |  para ver o histórico.

      .divider
      router-link.btn.btn-primary(:to="{ name: 'app.manuais' }")
        i.fa.fa-book.fa-fw
        |  Voltar à lista de manuais
</template>

<script>
export default {
  data() {
    return {
      imgLista: require("@/assets/manuais/41-config-formularios-lista.png"),
      imgChecklist: require("@/assets/manuais/41b-config-formularios-checklist.png"),
      imgCondicao: require("@/assets/manuais/41c-config-formularios-condicao.png"),
      imgReferencia: require("@/assets/manuais/41d-config-formularios-referencia.png"),
    }
  },
}
</script>

<style lang="stylus" scoped>
.manual-page
  h5
    margin-top 1.5rem

  .manual-figure
    margin 1rem 0
    img
      max-width 100%
      height auto
      border 1px solid #e7e9ed
      border-radius 4px

  .toast
    margin 1rem 0
</style>
